import React, { useEffect, useState } from 'react';
import { Plus, Trash2, Loader2, ToggleLeft, ToggleRight, Tag } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from '../../hooks/use-toast';
import api from '../../lib/api';

const empty = { code: '', discount_pct: '', expires_at: '', max_uses: '', active: true };

export default function CouponsSection() {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [deletingCode, setDeletingCode] = useState(null);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get('/admin/coupons'); setCoupons(data); }
    catch { toast({ title: 'Error al cargar cupones' }); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const patch = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.code.trim()) return toast({ title: 'Ingresa un código' });
    if (!form.discount_pct || isNaN(Number(form.discount_pct))) return toast({ title: 'Ingresa un % válido' });
    setSaving(true);
    try {
      await api.post('/admin/coupons', {
        code: form.code.trim().toUpperCase(),
        discount_pct: Number(form.discount_pct),
        expires_at: form.expires_at || null,
        max_uses: form.max_uses ? Number(form.max_uses) : null,
        active: true,
      });
      toast({ title: 'Cupón creado' });
      setForm(empty);
      load();
    } catch (e) {
      toast({ title: e?.response?.data?.detail || 'Error al crear' });
    } finally { setSaving(false); }
  };

  const toggleActive = async (c) => {
    try {
      await api.patch(`/admin/coupons/${c.code}`, { ...c, active: !c.active, discount_pct: Number(c.discount_pct), max_uses: c.max_uses || null });
      setCoupons((prev) => prev.map((x) => x.code === c.code ? { ...x, active: !x.active } : x));
    } catch { toast({ title: 'Error al actualizar' }); }
  };

  const del = async (code) => {
    setDeletingCode(code);
    try {
      await api.delete(`/admin/coupons/${code}`);
      setCoupons((prev) => prev.filter((c) => c.code !== code));
      toast({ title: 'Cupón eliminado' });
    } catch { toast({ title: 'Error al eliminar' }); }
    finally { setDeletingCode(null); }
  };

  const isExpired = (c) => {
    if (!c.expires_at) return false;
    try { return new Date(c.expires_at) < new Date(); } catch { return false; }
  };
  const isExhausted = (c) => c.max_uses != null && c.used_count >= c.max_uses;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-black text-white">Cupones</h2>
        <p className="text-white/50 text-sm mt-1">Crea códigos de descuento porcentual para tus clientes.</p>
      </div>

      {/* Create form */}
      <div className="bg-neutral-900 border border-white/10 rounded-2xl p-5 space-y-4">
        <h3 className="text-white font-bold flex items-center gap-2"><Tag className="w-4 h-4 text-orange-400" /> Nuevo cupón</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="md:col-span-1">
            <label className="text-[11px] uppercase tracking-wider font-bold text-white/50 mb-1 block">Código <span className="text-orange-400">*</span></label>
            <Input
              value={form.code} onChange={(e) => patch('code', e.target.value.toUpperCase())}
              placeholder="VERANO20" maxLength={20}
              className="bg-black/40 border-white/15 text-white uppercase tracking-widest font-bold"
              data-testid="coupon-code-input"
            />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider font-bold text-white/50 mb-1 block">Descuento % <span className="text-orange-400">*</span></label>
            <Input
              value={form.discount_pct} onChange={(e) => patch('discount_pct', e.target.value)}
              placeholder="10" type="number" min="1" max="100"
              className="bg-black/40 border-white/15 text-white"
              data-testid="coupon-pct-input"
            />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider font-bold text-white/50 mb-1 block">Expira</label>
            <Input
              value={form.expires_at} onChange={(e) => patch('expires_at', e.target.value)}
              type="date" className="bg-black/40 border-white/15 text-white"
              data-testid="coupon-expires-input"
            />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider font-bold text-white/50 mb-1 block">Límite usos</label>
            <Input
              value={form.max_uses} onChange={(e) => patch('max_uses', e.target.value)}
              placeholder="∞" type="number" min="1"
              className="bg-black/40 border-white/15 text-white"
              data-testid="coupon-maxuses-input"
            />
          </div>
        </div>
        <Button onClick={save} disabled={saving} className="bg-orange-500 hover:bg-orange-600 text-black font-bold rounded-full px-6" data-testid="coupon-save-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />} Crear cupón
        </Button>
      </div>

      {/* List */}
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-white/30" /></div>
      ) : coupons.length === 0 ? (
        <div className="text-center py-12 text-white/30">No hay cupones creados.</div>
      ) : (
        <div className="space-y-2" data-testid="coupons-list">
          {coupons.map((c) => {
            const expired = isExpired(c);
            const exhausted = isExhausted(c);
            const status = !c.active ? 'Inactivo' : expired ? 'Expirado' : exhausted ? 'Agotado' : 'Activo';
            const statusColor = status === 'Activo' ? 'text-emerald-400 bg-emerald-500/10 border-emerald-500/25'
              : 'text-white/40 bg-white/5 border-white/10';
            return (
              <div key={c.code} className="bg-neutral-900 border border-white/10 rounded-2xl px-5 py-4 flex flex-wrap items-center gap-4" data-testid={`coupon-row-${c.code}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="font-black text-white tracking-widest text-sm font-mono">{c.code}</span>
                    <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full border ${statusColor}`}>{status}</span>
                  </div>
                  <div className="flex gap-4 mt-1 text-xs text-white/50">
                    <span className="text-orange-400 font-bold">{c.discount_pct}% desc.</span>
                    {c.expires_at && <span>Expira {new Date(c.expires_at).toLocaleDateString('es-MX')}</span>}
                    <span>{c.used_count ?? 0} {c.max_uses != null ? `/ ${c.max_uses}` : ''} usos</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => toggleActive(c)} className="text-white/40 hover:text-orange-400 transition" title={c.active ? 'Desactivar' : 'Activar'} data-testid={`coupon-toggle-${c.code}`}>
                    {c.active ? <ToggleRight className="w-6 h-6 text-emerald-400" /> : <ToggleLeft className="w-6 h-6" />}
                  </button>
                  <button onClick={() => del(c.code)} disabled={deletingCode === c.code} className="text-white/30 hover:text-rose-400 transition" data-testid={`coupon-delete-${c.code}`}>
                    {deletingCode === c.code ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
